import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { CheckboxField } from '@openx/components-core/lib/CheckboxField/CheckboxField.tsx';
import type { Option } from '@openx/types/options.ts';
import { type ChangeEvent, useCallback, useMemo } from 'react';
import {
  handleChildChange,
  handleChildUnchange,
  toggleExpandedSet,
  updateParentSelection,
} from '../../utils/hierarchicalOption/hierarchicalUtils.ts';

type HierarchicalOptionProps = {
  option: Option;
  level: number;
  parentOption?: Option;
  selectedItems: string[];
  onOptionChange: (options: string | string[]) => void;
  isDisabled?: boolean;
  expanded: Set<string>;
  setExpanded: React.Dispatch<React.SetStateAction<Set<string>>>;
};

export const HierarchicalOption = ({
  option,
  level,
  parentOption,
  selectedItems,
  onOptionChange,
  isDisabled,
  expanded,
  setExpanded,
}: HierarchicalOptionProps) => {
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, optionId: string, parentOption?: Option) => {
      const newSelection = e.target.checked
        ? handleChildChange(selectedItems, optionId, parentOption)
        : handleChildUnchange(selectedItems, optionId, parentOption);
      onOptionChange(newSelection);
    },
    [selectedItems, onOptionChange]
  );

  const handleParentChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, option: Option) => {
      const newSelection = updateParentSelection(selectedItems, option, e.target.checked);
      onOptionChange(newSelection);
    },
    [selectedItems, onOptionChange]
  );

  const selectedSet = useMemo(() => new Set(selectedItems), [selectedItems]);
  const childrenIds = useMemo(() => option.children?.map(c => c.id) || [], [option.children]);

  const allChildrenSelected = childrenIds.length > 0 && childrenIds.every(id => selectedSet.has(id));
  const someChildrenSelected = childrenIds.length > 0 && childrenIds.some(id => selectedSet.has(id));
  const selectedCount = childrenIds.filter(id => selectedSet.has(id)).length;

  const label = childrenIds.length > 0 && someChildrenSelected ? `${option.name} (${selectedCount})` : option.name;

  const isExpanded = expanded.has(option.id);
  const isChecked = selectedSet.has(option.id) || (childrenIds.length > 0 && allChildrenSelected);

  const toggleExpanded = () => {
    setExpanded(prev => toggleExpandedSet(prev, option.id));
  };

  return (
    <Stack key={option.id} style={{ paddingLeft: `${level * 24}px` }}>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
        }}
      >
        <CheckboxField
          data-test={`hierarchical-checkbox-option-${label.toLowerCase()}`}
          label={label}
          checked={isChecked}
          indeterminate={someChildrenSelected && !allChildrenSelected}
          onChange={e => {
            if (childrenIds.length > 0) {
              handleParentChange(e, option);
            } else {
              handleChange(e, option.id, parentOption);
            }
          }}
          disabled={isDisabled}
        />
        {childrenIds.length > 0 && (
          <IconButton
            data-test={`hierarchical-expand-toggle-button-${label.toLowerCase()}`}
            size="small"
            onClick={toggleExpanded}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        )}
      </Stack>
      {isExpanded &&
        option.children?.map(child => (
          <HierarchicalOption
            key={child.id}
            option={child}
            level={level + 1}
            parentOption={option}
            selectedItems={selectedItems}
            onOptionChange={onOptionChange}
            isDisabled={isDisabled}
            expanded={expanded}
            setExpanded={setExpanded}
          />
        ))}
    </Stack>
  );
};
